import axios from "axios";
import apiClient from "../../api/axiosInstance";
import { getLegalConsentConfig } from "../../pages/docs/config/legal";
import { isLegalLocalMode } from "../../pages/docs/constants/legalEnv";
import { STATUS, extractErrorInfo } from "./sharedfunction";

function resolveLegalContentUrl({ contentUrl, s3_folder } = {}) {
  if (contentUrl) {
    return contentUrl;
  }
  const base = (process.env.REACT_APP_DOCS_CDN_URL ?? "").replace(/\/$/, "");
  if (base && s3_folder) {
    return `${base}/${String(s3_folder).replace(/^\//, "")}`;
  }
  return null;
}

function logFetchError(message, error) {
  if (process.env.NODE_ENV === "development") {
    console.error(message, error);
  }
}

let legalMetaCache = null;
let legalMetaCacheExpiry = 0;
const LEGAL_META_CACHE_MS = 60_000;

/**
 * Fetch current ToS + Privacy metadata from backend.
 * @returns {Promise<{ status: number, data?: Array, error?: string, code?: string }>}
 */
export async function fetchCurrentLegalDocuments() {
  const now = Date.now();
  if (legalMetaCache && now < legalMetaCacheExpiry) {
    return legalMetaCache;
  }

  try {
    const response = await apiClient.get("/legal/current");

    let result;
    if (response?.data?.success) {
      result = {
        status: STATUS.SUCCESS,
        data: response?.data?.data?.documents ?? [],
        message: response?.data?.message,
      };
    } else {
      result = {
        status: STATUS.ERROR,
        error: response?.data?.message ?? response?.data?.error,
        code: response?.data?.code,
      };
    }

    if (result.status === STATUS.SUCCESS) {
      legalMetaCache = result;
      legalMetaCacheExpiry = now + LEGAL_META_CACHE_MS;
    }

    return result;
  } catch (error) {
    return extractErrorInfo(error);
  }
}

/**
 * Load legal consent body (local bundle or CDN contentUrl).
 * @param {'tos'|'privacy'} documentType
 * @param {string|null} contentUrl
 */
export async function loadLegalConsentContent(documentType, contentUrl = null) {
  if (isLegalLocalMode()) {
    const config = getLegalConsentConfig(documentType);
    if (!config?.blocks?.length) {
      return { ok: false, error: new Error(`Missing local legal config for ${documentType}`) };
    }
    return { ok: true, data: config };
  }

  if (!contentUrl) {
    return { ok: false, error: new Error("Legal content URL is required in S3 mode") };
  }

  try {
    const { data } = await axios.get(contentUrl);
    if (!data?.blocks?.length) {
      throw new Error("Legal content is missing blocks");
    }
    return { ok: true, data };
  } catch (error) {
    logFetchError(`[legal] Failed to load ${documentType} from CDN`, error);
    return { ok: false, error };
  }
}

/**
 * Resolve version IDs for both documents from current legal metadata.
 */
export function getLegalVersionIds(documents = []) {
  const tos = documents.find((doc) => doc.document_type === "tos");
  const privacy = documents.find((doc) => doc.document_type === "privacy");

  return {
    tosLegalDocumentVersionId: tos?.legal_document_version_id ?? null,
    privacyLegalDocumentVersionId: privacy?.legal_document_version_id ?? null,
    tosContentUrl: resolveLegalContentUrl(tos),
    privacyContentUrl: resolveLegalContentUrl(privacy),
  };
}

/**
 * Load legal document for public /terms and /privacy pages.
 * Local mode uses bundled JSON (no API required); S3 mode uses API metadata + CDN.
 */
export async function loadLegalDocumentForPage(documentType) {
  if (isLegalLocalMode()) {
    return loadLegalConsentContent(documentType);
  }

  const metaResponse = await fetchCurrentLegalDocuments();
  if (metaResponse.status !== STATUS.SUCCESS) {
    return { ok: false, error: new Error(metaResponse.error || "Failed to load legal documents") };
  }

  const documentMeta = metaResponse.data?.find((doc) => doc.document_type === documentType);
  if (!documentMeta) {
    return { ok: false, error: new Error(`Missing legal document metadata for ${documentType}`) };
  }

  return loadLegalConsentContent(documentType, resolveLegalContentUrl(documentMeta));
}

/**
 * Synchronous local bundle lookup for public legal pages (getting-started pattern).
 */
export function getLocalLegalDocumentConfig(documentType) {
  if (!isLegalLocalMode()) {
    return null;
  }
  return getLegalConsentConfig(documentType);
}
